import { getLanguageDetails, Language } from './language';

export const formatRelativeTime = (
  date: Date,
  language: Language,
  customDateFormat?: Intl.DateTimeFormatOptions
): string => {
  const locale = getLanguageDetails(language)?.locale || 'fa-IR';
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (diffInSeconds < 60) {
    return rtf.format(-diffInSeconds, 'second');
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return rtf.format(-diffInMinutes, 'minute');
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return rtf.format(-diffInHours, 'hour');
  }

  const defaultDateFormat: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: 'numeric',
    timeZone: 'Europe/London',
    timeZoneName: 'short',
    hour12: false,
  };

  return date.toLocaleDateString(locale, customDateFormat || defaultDateFormat);
}

export function hasTimeExceeded(
  inputDate?: string | Date | null,
  threshold: string = '2 hours'
): boolean {
  if (!inputDate) return false;

  const targetTime = new Date(inputDate).getTime();
  if (isNaN(targetTime)) return false;

  const now = Date.now();
  const elapsed = Date.now() - targetTime;

  const thresholdMs = parseThresholdToMilliseconds(threshold);
  return elapsed > thresholdMs;
}

function parseThresholdToMilliseconds(threshold: string): number {
  const pattern = /^(\d+)\s*(second|minute|hour|day|week|month|year)s?$/i;
  const match = threshold.trim().match(pattern);

  if (!match) {
    throw new Error(`Invalid threshold format: "${threshold}". Use e.g. "2 hours" or "1 day".`);
  }

  const [, valueStr, unitRaw] = match;
  const value = parseInt(valueStr, 10);
  const unit = unitRaw.toLowerCase();

  const millisecondsPerUnit: Record<string, number> = {
    second: 1_000,
    minute: 60_000,
    hour: 3_600_000,
    day: 86_400_000,
    week: 604_800_000,
    month: 2_592_000_000, // 30 days
    year: 31_536_000_000, // 365 days
  };

  const unitMs = millisecondsPerUnit[unit];
  if (!unitMs) {
    throw new Error(`Unsupported time unit: "${unit}".`);
  }

  return value * unitMs;
}